import React, { useState, FormEvent, ChangeEvent, useEffect } from "react";

import ReactPixel from "react-facebook-pixel";
import {
  FaBirthdayCake,
  FaPhone,
  FaCheckCircle,
  FaWhatsapp,
  FaChevronDown,
} from "react-icons/fa";

import Section from "./Section";
import { citiesList } from "../data/cities";
import { FormFields, SubmitData } from "../types/form";
import {
  trackFormStart,
  trackFormSubmit,
  trackFormError,
  trackSuccessWhatsAppClick,
} from "../utils/analytics";

interface SubmitStatus {
  type: "success" | "error" | "unsubmitted";
  message: string;
}

interface FormResponse {
  success: boolean;
  message?: string;
}

ReactPixel.init("2593272121055256");

const SignupForm: React.FC = () => {
  const [formData, setFormData] = useState<FormFields>({
    fullName: "",
    whatsappNumber: "",
    age: undefined,
    city: "",
  });
  const [ageError, setAgeError] = useState<string>("");
  const [phoneError, setPhoneError] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [submitStatus, setSubmitStatus] = useState<SubmitStatus>({
    type: "unsubmitted",
    message: "",
  });
  const [hasTrackedFormStart, setHasTrackedFormStart] =
    useState<boolean>(false);
  const [hasSubmittedSuccessfully, setHasSubmittedSuccessfully] =
    useState<boolean>(false);

  // Handle URL parameters to pre-populate city
  useEffect(() => {
    const checkForCityParam = () => {
      const urlParams = new URLSearchParams(window.location.search);
      const cityParam = urlParams.get("city");

      if (cityParam) {
        // Check if the city exists in our cities list
        const cityExists = citiesList.some(
          (city) => city.name.toLowerCase() === cityParam.toLowerCase()
        );

        if (cityExists) {
          setFormData((prev) => ({
            ...prev,
            city: cityParam,
          }));
        }
      }
    };

    // Check on mount
    checkForCityParam();

    // Listen for URL changes (popstate event)
    const handleUrlChange = () => {
      checkForCityParam();
    };

    // Listen for custom URL change event from map
    const handleCustomUrlChange = (event: CustomEvent) => {
      const cityName = event.detail?.city;
      if (cityName) {
        const cityExists = citiesList.some(
          (city) => city.name.toLowerCase() === cityName.toLowerCase()
        );
        if (cityExists) {
          setFormData((prev) => ({
            ...prev,
            city: cityName,
          }));
        }
      }
    };

    window.addEventListener("popstate", handleUrlChange);
    window.addEventListener(
      "urlChanged",
      handleCustomUrlChange as EventListener
    );

    // Also check when the component becomes visible (in case URL was updated via history.replaceState)
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        checkForCityParam();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener("popstate", handleUrlChange);
      window.removeEventListener(
        "urlChanged",
        handleCustomUrlChange as EventListener
      );
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  const handleInputChange = (field: keyof FormFields, value: string): void => {
    // Prevent changes after successful submission
    if (hasSubmittedSuccessfully) {
      return;
    }

    // Track form start on first input interaction
    if (!hasTrackedFormStart) {
      trackFormStart();
      setHasTrackedFormStart(true);
    }

    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Validate age when age field changes
    if (field === "age") {
      validateAge(value);
    }

    // Validate phone when WhatsApp number field changes
    if (field === "whatsappNumber") {
      validatePhone(value);
    }
  };

  const validateAge = (age: string): void => {
    if (age && parseInt(age) < 18) {
      setAgeError("Age must be greater than 18");
    } else {
      setAgeError("");
    }
  };

  const validatePhone = (phone: string): void => {
    const phoneRegex = /^[2-9][0-9]{9}$/;
    if (phone && !phoneRegex.test(phone)) {
      setPhoneError("Please enter a valid 10-digit phone number");
    } else {
      setPhoneError("");
    }
  };

  const calculateProgress = (): number => {
    let completed = 0;
    const total = 4; // Total steps: name, age, phone

    if (formData.fullName.trim()) completed++;
    if (formData.age && !ageError) completed++;
    if (formData.whatsappNumber.trim() && !phoneError) completed++;
    if (formData.city) completed++;

    return Math.round((completed / total) * 100);
  };

  const isFormValid = (): boolean => {
    return (
      formData.fullName.trim() !== "" &&
      formData.whatsappNumber.trim() !== "" &&
      formData.age !== undefined &&
      formData.city !== "" &&
      !ageError &&
      !phoneError &&
      !hasSubmittedSuccessfully
    );
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();

    if (!isFormValid() || hasSubmittedSuccessfully) {
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus({ type: "unsubmitted", message: "" });

    try {
      const submitFormData = new FormData();
      submitFormData.append("fullName", formData.fullName);
      submitFormData.append("age", String(formData.age));
      submitFormData.append("phonePrefix", "+91");
      submitFormData.append("whatsappNumber", formData.whatsappNumber);
      submitFormData.append("city", formData.city);

      const response = await fetch("/api/submit-form", {
        method: "POST",
        body: submitFormData,
      });

      const result: FormResponse = await response.json();

      if (response.ok && result.success) {
        const submitData: SubmitData = {
          ...formData,
          phonePrefix: "+91",
        };
        trackFormSubmit(submitData);
        ReactPixel.track("Lead", {
          fn: formData.fullName.toLowerCase(),
          ge: "f",
          ph: "91" + formData.whatsappNumber,
        });
        setHasSubmittedSuccessfully(true);
        setSubmitStatus({
          type: "success",
          message:
            "Thank you! Your application has been submitted successfully.",
        });
      } else {
        throw new Error(result.message || "Submission failed");
      }
    } catch (error) {
      console.error("Form submission error:", error);
      trackFormError("validation", (error as Error).message);
      setSubmitStatus({
        type: "error",
        message:
          "There was an error submitting your application. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Section
      id="signup-form"
      height="large"
      className="border-b border-pink-200"
    >
      <div className="max-w-lg w-full">
        {hasSubmittedSuccessfully ? (
          /* Success State - Form Hidden */
          <div className="text-center space-y-8">
            <div className="relative">
              <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-r from-pink-400 to-purple-500 rounded-full flex items-center justify-center shadow-lg">
                <FaCheckCircle className="text-4xl text-white" />
              </div>
            </div>

            <h3 className="text-3xl font-bold text-purple-800 font-display">
              Thank you for requesting exclusive access to Cohop!
            </h3>

            <p className="text-purple-700 text-lg leading-relaxed">
              Our team will reach out shortly to verify your details. Stay
              tuned! In the meantime, you can request to join our WhatsApp
              Community.
            </p>

            {/* WhatsApp Community Button */}
            <div className="pt-4">
              <a
                href={
                  citiesList.find((city) => city.name === formData.city)
                    ?.whatsappGroupLink || citiesList[2].whatsappGroupLink
                }
                target="_blank"
                rel="noopener noreferrer"
                onClick={trackSuccessWhatsAppClick}
                className="inline-flex items-center justify-center gap-3 px-8 py-4 bg-gradient-to-r from-pink-400 to-purple-500 hover:from-pink-500 hover:to-purple-600 text-white font-semibold rounded-xl transition-all duration-300 shadow-lg hover:shadow-pink-500/25 transform hover:scale-105"
              >
                <FaWhatsapp className="text-2xl" />
                <span>
                  Join the {formData.city || "Bengaluru"} Cohop WhatsApp
                  Community
                </span>
              </a>
            </div>
          </div>
        ) : (
          /* Form State */
          <>
            <h3 className="text-3xl font-semibold text-purple-800 text-center mb-8 font-display">
              Request Exclusive Access
            </h3>

            {/* Progress Bar */}
            <div className="mb-6">
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className={`h-2 rounded-full transition-all duration-300 ${
                    hasSubmittedSuccessfully
                      ? "bg-gradient-to-r from-pink-400 to-purple-500"
                      : calculateProgress() === 100
                        ? "bg-purple-500"
                        : "bg-gradient-to-r from-pink-400 to-purple-500"
                  }`}
                  style={{ width: `${calculateProgress()}%` }}
                ></div>
              </div>
            </div>

            <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 sm:p-8 shadow-xl border border-white/50">
              <form className="space-y-6" onSubmit={handleSubmit}>
                {/* Full Name Input */}
                <div className="relative group">
                  <input
                    type="text"
                    placeholder="Full Name"
                    value={formData.fullName}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      handleInputChange("fullName", e.target.value)
                    }
                    readOnly={hasSubmittedSuccessfully}
                    className={`w-full pl-4 pr-8 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:border-transparent text-gray-800 placeholder-gray-400 transition-all duration-300 ${
                      hasSubmittedSuccessfully
                        ? "border-purple-200 bg-purple-50/90 cursor-not-allowed"
                        : "border-pink-200 bg-white/90 focus:ring-purple-400"
                    }`}
                  />
                </div>

                {/* Age Input */}
                <div className="relative group">
                  <input
                    type="number"
                    placeholder="Age"
                    value={formData.age}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      handleInputChange("age", e.target.value)
                    }
                    min="18"
                    max="65"
                    readOnly={hasSubmittedSuccessfully}
                    className={`w-full pl-4 pr-12 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:border-transparent text-gray-800 placeholder-gray-400 transition-all duration-300 ${
                      hasSubmittedSuccessfully
                        ? "border-purple-200 bg-purple-50/90 cursor-not-allowed"
                        : "border-pink-200 bg-white/90 focus:ring-purple-400"
                    }`}
                  />
                  <div className="absolute inset-y-0 right-0 pr-3 pt-4 pointer-events-none">
                    <FaBirthdayCake className="h-5 w-5 text-pink-400" />
                  </div>
                  {ageError && (
                    <p className="text-red-500 text-sm mt-1 min-h-[20px]">
                      {ageError}
                    </p>
                  )}
                </div>

                {/* WhatsApp Number Input */}
                <div className="relative group">
                  <div className="flex w-full overflow-hidden">
                    <span className="flex-shrink-0 inline-flex items-center px-2 py-3 rounded-l-xl border border-r-0 border-pink-200 bg-gray-50 text-gray-500 text-sm whitespace-nowrap">
                      +91
                    </span>
                    <div className="relative flex-1 min-w-0">
                      <input
                        type="tel"
                        placeholder="WhatsApp Number"
                        value={formData.whatsappNumber}
                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                          handleInputChange("whatsappNumber", e.target.value)
                        }
                        readOnly={hasSubmittedSuccessfully}
                        className={`w-full pl-3 pr-9 py-3 border rounded-r-xl focus:outline-none focus:ring-2 focus:border-transparent text-gray-800 placeholder-gray-400 transition-all duration-300 ${
                          hasSubmittedSuccessfully
                            ? "border-purple-200 bg-purple-50/90 cursor-not-allowed"
                            : "border-pink-200 bg-white/90 focus:ring-purple-400"
                        }`}
                      />
                      <div className="absolute inset-y-0 right-0 pr-2 flex items-center pointer-events-none">
                        <FaPhone className="h-4 w-4 text-pink-400" />
                      </div>
                    </div>
                  </div>
                  {phoneError && (
                    <p className="text-red-500 text-sm mt-1 min-h-[20px]">
                      {phoneError}
                    </p>
                  )}
                </div>

                {/* City Selection */}
                <div className="relative group">
                  <div className="flex w-full relative">
                    <select
                      value={formData.city}
                      onChange={(e: ChangeEvent<HTMLSelectElement>) => {
                        handleInputChange("city", e.target.value);
                      }}
                      disabled={hasSubmittedSuccessfully}
                      className={`w-full pl-4 pr-10 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:border-transparent text-gray-800 appearance-none transition-all duration-300 ${
                        hasSubmittedSuccessfully
                          ? "border-purple-200 bg-purple-50/90 cursor-not-allowed"
                          : "border-pink-200 bg-white/90 focus:ring-purple-400"
                      } ${!formData.city ? "text-gray-400" : ""}`}
                    >
                      <option value="" disabled>
                        Select your city
                      </option>
                      {citiesList.map((city) => (
                        <option key={city.name} value={city.name}>
                          {city.name}
                        </option>
                      ))}
                    </select>
                    <div className="absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none">
                      <FaChevronDown className="h-4 w-4 text-pink-400 transform transition-transform duration-200 ease-in-out group-focus-within:rotate-180" />
                    </div>
                  </div>
                </div>

                {/* Submit Status */}
                {submitStatus.message && (
                  <div
                    className={`p-4 rounded-xl ${
                      submitStatus.type === "success"
                        ? "bg-green-50 text-green-700 border border-green-200"
                        : "bg-red-50 text-red-700 border border-red-200"
                    }`}
                  >
                    {submitStatus.message}
                  </div>
                )}

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={
                    !isFormValid() || isSubmitting || hasSubmittedSuccessfully
                  }
                  className={`w-full py-4 px-6 rounded-xl font-semibold text-lg transition-all duration-300 ${
                    hasSubmittedSuccessfully
                      ? "bg-gradient-to-r from-pink-400 to-purple-500 text-white cursor-not-allowed"
                      : isFormValid() && !isSubmitting
                        ? "bg-gradient-to-r from-pink-400 to-purple-500 hover:from-pink-500 hover:to-purple-600 text-white shadow-lg hover:shadow-pink-500/25 transform hover:scale-105"
                        : "bg-gray-300 text-gray-500 cursor-not-allowed"
                  }`}
                >
                  {hasSubmittedSuccessfully ? (
                    <span className="flex items-center justify-center gap-2">
                      <FaCheckCircle className="text-xl" />
                      <span>Application Submitted!</span>
                    </span>
                  ) : isSubmitting ? (
                    <span className="flex items-center justify-center gap-2">
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Submitting...
                    </span>
                  ) : (
                    "Request Exclusive Access"
                  )}
                </button>
              </form>
            </div>
          </>
        )}
      </div>
    </Section>
  );
};

export default SignupForm;
